import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";

import type { ConfigDTO } from "../../../common/types/ConfigDTO";
import tokenManager from "../../utils/tokenManager";
import { configService } from "../services/config";

type ConfigUriKey = keyof Pick<
  ConfigDTO,
  "aiakosUri" | "zeusUri" | "hermesUri" | "orpheusUri" | "baseApiUri" | "bffApiUri"
>;

// Optional per-call header layer. String values set or replace; `null`
// deletes a default header (e.g. dropping content-type so the browser can
// supply a multipart boundary).
export type CustomHeaders = Record<string, string | null>;

// FetchArgs extension — endpoints can pass `customHeaders` alongside the
// usual fields and they'll be applied on top of the default auth layer.
export type ServiceFetchArgs = FetchArgs & { customHeaders?: CustomHeaders };

// JWT auth headers in plain-object form. Used by the factories below (via
// `prepareHeaders`) and also exported for non-RTK consumers (AI SDK chat
// transport, raw axios calls). Single source of truth for what auth headers
// nav-originated requests carry.
export const getApiHeaders = (): Record<string, string> => {
  const jwt = tokenManager.getToken();
  return jwt ? { authorization: `Bearer ${jwt}` } : {};
};

// Factory that produces a `prepareHeaders` function for fetchBaseQuery.
//
// Default layer: JWT auth headers. Optional layer: per-call custom headers
// that can add, override, or delete (via `null`) entries. Returning the
// inner `(headers) => Headers` directly matches fetchBaseQuery's
// prepareHeaders signature so it can be assigned without a wrapper — and
// without risk of the api object being misread as customHeaders.
//
// Content-type is intentionally not in the default layer — fetchBaseQuery
// sets `application/json` automatically when the body is a plain object,
// and leaves it unset for FormData/Blob/ArrayBuffer so the browser can
// supply `multipart/form-data; boundary=...` itself.
const buildHeaders =
  (customHeaders?: CustomHeaders) =>
  (headers: Headers): Headers => {
    for (const [key, value] of Object.entries(getApiHeaders())) {
      headers.set(key, value);
    }
    if (customHeaders) {
      for (const [key, value] of Object.entries(customHeaders)) {
        if (value === null) {
          headers.delete(key);
        } else {
          headers.set(key, value);
        }
      }
    }
    return headers;
  };

// Reads `<service>Uri` from runtime config (loaded by configService) and
// composes the request URL. `bffApiUri` targets endpoints owned by nav's
// BFF; the other keys (`aiakosUri`/`zeusUri`/`orpheusUri`/`hermesUri`/
// `baseApiUri`) target backends directly via Traefik.
export const createServiceBaseQuery = (
  uriKey: ConfigUriKey,
): BaseQueryFn<string | ServiceFetchArgs, unknown, FetchBaseQueryError> => {
  return async (args, api, extraOptions) => {
    const state = api.getState();
    // configService is registered in nav's store; data is populated by the
    // initial `useGetConfigQuery()` call in `useInit`. Routes that consume
    // these services are gated behind that init, so config is non-null when
    // we reach here. Empty-string fallback keeps fetchBaseQuery from
    // throwing if it ever isn't.
    const config = configService.endpoints.getConfig.select()(
      state as Parameters<ReturnType<typeof configService.endpoints.getConfig.select>>[0],
    ).data;
    const baseUrl = config?.[uriKey] ?? "";

    const customHeaders = typeof args === "object" ? args.customHeaders : undefined;

    const rawQuery = fetchBaseQuery({
      baseUrl,
      prepareHeaders: buildHeaders(customHeaders),
    });

    // Strip the extension field so fetchBaseQuery doesn't propagate it into
    // RequestInit (fetch ignores unknown props but cleaner not to forward).
    if (typeof args === "object" && "customHeaders" in args) {
      const { customHeaders: _customHeaders, ...rest } = args;
      return rawQuery(rest, api, extraOptions);
    }
    return rawQuery(args, api, extraOptions);
  };
};

// Auth-headers-only base query. Used by services whose URL is supplied
// per-request as an absolute URI from runtime config (OIDC `verifyTokenUri`,
// `userInfoUri`). `baseUrl` is "/" so relative URIs resolve against the
// current origin; absolute URIs (the production case) ignore baseUrl entirely.
// Return type is inferred from fetchBaseQuery so callers can read
// `meta.response` in `transformResponse`.
export const createAuthBaseQuery = () =>
  fetchBaseQuery({
    baseUrl: "/",
    prepareHeaders: buildHeaders(),
  });
